import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  User, LogOut, Home, Calendar, Newspaper, Heart, 
  Edit, Save, X, Loader2, Mail, Phone, Clock, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export default function PortailAdherent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  const getToken = () => localStorage.getItem('briouso_token');
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getToken();
    if (!token) {
      navigate('/');
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setUser(res.data);
      setEditForm({
        first_name: res.data.first_name,
        last_name: res.data.last_name,
        phone: res.data.phone || ''
      });
      
      // Fetch events and news
      const [eventsRes, newsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/news`)
      ]);
      setEvents(eventsRes.data.slice(0, 3));
      setNews(newsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('briouso_token');
      localStorage.removeItem('briouso_user');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('briouso_token');
    localStorage.removeItem('briouso_user');
    toast.info(t('common.logoutSuccess'));
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/adherent/profile`, editForm, getAuthHeaders());
      setUser({ ...user, ...editForm });
      localStorage.setItem('briouso_user', JSON.stringify({ ...user, ...editForm }));
      setEditing(false);
      toast.success(t('common.savedSuccess'));
    } catch (error) {
      toast.error(t('common.savedError'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-lime-50/50 to-white" data-testid="portail-adherent">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <User className="h-8 w-8 text-lime-600" />
              <h1 className="font-['Playfair_Display'] text-3xl font-bold text-zinc-900">
                Mon Espace Adhérent
              </h1>
            </div>
            <p className="text-zinc-600">
              Bienvenue, {user.first_name} {user.last_name}!
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/')} className="rounded-full">
              <Home className="h-4 w-4 mr-2" />
              Accueil
            </Button>
            <Button variant="outline" onClick={handleLogout} className="rounded-full text-red-600 hover:text-red-700">
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-lime-600" />
                  Mon Profil
                </CardTitle>
                {!editing ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!editing ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-zinc-500">Nom complet</p>
                    <p className="font-medium">{user.first_name} {user.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 flex items-center gap-1">
                      <Mail className="h-3 w-3" /> Email
                    </p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 flex items-center gap-1">
                      <Phone className="h-3 w-3" /> Téléphone
                    </p>
                    <p className="font-medium">{user.phone || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Membre depuis
                    </p>
                    <p className="font-medium">{formatDate(user.created_at)}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Prénom</Label>
                    <Input 
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Nom</Label>
                    <Input 
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input 
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    />
                  </div>
                  <Button 
                    onClick={handleSaveProfile} 
                    disabled={saving}
                    className="w-full bg-lime-600 hover:bg-lime-700"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Enregistrer
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Events Card */}
          <Card className="border-0 shadow-lg lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-fuchsia-600" />
                Prochains Événements
              </CardTitle>
              <CardDescription>Ne manquez rien de l'association</CardDescription>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-zinc-500 text-center py-4">Aucun événement à venir</p>
              ) : (
                <div className="space-y-3">
                  {events.map((event, index) => (
                    <div key={event.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                      <div>
                        <p className="font-medium text-zinc-900">{event.title}</p>
                        <p className="text-sm text-zinc-500">{formatDate(event.date)} {event.time && `à ${event.time}`}</p>
                      </div>
                      {event.reservation_url && (
                        <a 
                          href={event.reservation_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-fuchsia-600 hover:text-fuchsia-700 font-medium"
                        >
                          Réserver
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate('/evenements')}
              >
                Voir tous les événements
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* News Section */}
        <Card className="border-0 shadow-lg mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-indigo-600" />
              Dernières Actualités
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {news.map((item) => (
                <div key={item.id} className="p-4 bg-zinc-50 rounded-lg">
                  <h4 className="font-medium text-zinc-900 line-clamp-2">{item.title}</h4>
                  <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{item.excerpt}</p>
                  <p className="text-xs text-zinc-400 mt-2">{formatDate(item.published_at)}</p>
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/actualites')}
            >
              Voir toutes les actualités
            </Button>
          </CardContent>
        </Card>

        {/* Advantages Section */}
        <Card className="border-0 shadow-lg mt-6 bg-gradient-to-r from-lime-50 to-fuchsia-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Vos Avantages Adhérent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white/80 rounded-lg">
                <Heart className="h-6 w-6 text-fuchsia-600 mb-2" />
                <h4 className="font-medium text-zinc-900">Tarifs Préférentiels</h4>
                <p className="text-sm text-zinc-500">Réductions sur nos événements et stages</p>
              </div>
              <div className="p-4 bg-white/80 rounded-lg">
                <Calendar className="h-6 w-6 text-lime-600 mb-2" />
                <h4 className="font-medium text-zinc-900">Accès Prioritaire</h4>
                <p className="text-sm text-zinc-500">Réservation anticipée aux événements</p>
              </div>
              <div className="p-4 bg-white/80 rounded-lg">
                <Newspaper className="h-6 w-6 text-indigo-600 mb-2" />
                <h4 className="font-medium text-zinc-900">Newsletter VIP</h4>
                <p className="text-sm text-zinc-500">Actualités exclusives en avant-première</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
